import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { ConsigneeType } from "./customs";

const FulfillmentOrderAddressSchema_ = z
  .object({
    address1: z.string().meta({ privacy: privacy.address_line }).nullish(),
    address2: z.string().meta({ privacy: privacy.address_line }).nullish(),
    company: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    city: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    countryCode: createEnum(RedoShopifyCountryCode, {
      enumName: "RedoShopifyCountryCode",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    locationId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    name: z.string().meta({ privacy: privacy.person_name }),
    phone: z.string().meta({ privacy: privacy.phone }).nullish(),
    email: z.string().meta({ privacy: privacy.email }).nullish(),
    province: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    zip: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    residential: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    verified: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    consigneeType: createEnum(ConsigneeType, { enumName: "ConsigneeType" })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    messages: z
      .array(z.string().meta({ privacy: privacy.redact }))
      .nullish()
      .meta({ privacy: privacy.null }),
  })
  .meta({ privacy: privacy.structured });

export const FulfillmentOrderAddressSchema: z.ZodType<FulfillmentOrderAddress> =
  FulfillmentOrderAddressSchema_;

export interface FulfillmentOrderAddress extends z.infer<
  typeof FulfillmentOrderAddressSchema_
> {}

export const FulfillmentOrderAddressWithIdSchema =
  FulfillmentOrderAddressSchema_.extend({
    _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
  });

export type FulfillmentOrderAddressWithId = z.infer<
  typeof FulfillmentOrderAddressWithIdSchema
>;

//https://lorefnon.me/2023/11/28/fixing-inferred-types-exceeding-serializable-length/
export interface IFulfillmentOrderAddress extends z.ZodType<FulfillmentOrderAddress> {}

const VerifiedAddressSchema_ = FulfillmentOrderAddressSchema_.extend({
  countryCode: z.string().meta({ privacy: privacy.nonPii }).nullish(),
});
export const VerifiedAddressSchema: z.ZodType<VerifiedAddress> =
  VerifiedAddressSchema_;
export interface VerifiedAddress extends z.infer<
  typeof VerifiedAddressSchema_
> {}

// When comparing addresses, only these keys matter.
const equalityKeys: (keyof FulfillmentOrderAddress)[] = [
  "name",
  "email",
  "phone",
  "company",
  "address1",
  "address2",
  "city",
  "province",
  "zip",
  "countryCode",
  "residential",
];
export const addressesEqual = (
  a: FulfillmentOrderAddress | VerifiedAddress,
  b: FulfillmentOrderAddress | VerifiedAddress,
) => equalityKeys.every((k) => (a[k] || null) === (b[k] || null));

// Address fields a merchant edits in the address modal. System-derived flags
// (residential, verified) are intentionally excluded so they cannot trigger
// activity-log entries with an empty diff. Keep in sync with the merchant-app
// label map in shipping-address-diff.ts.
export const userVisibleAddressKeys = [
  "name",
  "company",
  "address1",
  "address2",
  "city",
  "province",
  "zip",
  "countryCode",
  "phone",
  "email",
] as const satisfies readonly (keyof FulfillmentOrderAddress)[];

export type UserVisibleAddressKey = (typeof userVisibleAddressKeys)[number];

export const addressesHaveUserVisibleDifference = (
  a: FulfillmentOrderAddress | VerifiedAddress,
  b: FulfillmentOrderAddress | VerifiedAddress,
) => userVisibleAddressKeys.some((k) => (a[k] || null) !== (b[k] || null));
